angular.module('content.widgets.ChoiceDecisionWidgetService', [
])

/**
 * The ChoiceDecisionWidgetService service handles scoring and
 * feedback for the state.learningOutcomes type
 *
 * @class ChoiceDecisionWidgetService
 * @constructor
 * @module content.widgets.ChoiceDecisionWidgetService
 */
.factory('ChoiceDecisionWidgetService', [
  '$rootScope',
  'EventsWidgetService',
  'ModelsService',
  'StatesService',
  function ($rootScope, EventsWidgetService, ModelsService, StatesService) {
    'use strict';

    var service = {

      score: {
        points: {
          current: 0,
          total: 0
        }
      },

      feedback: {},

      disabled: [],

      /**
       * Processes the scoring and feedback of
       * state.ChoiceDecision item types that
       * are single answer.
       *
       * @param {string} stateId - ID of the state
       * @param {string} decision - Decision Content
       * @param {string} choice - Selected choice
       * @param {boolean} reset - Reset values
       * @return {Promise<Object>} Full feedback object resulting from process
       */
      processSingle: function processSingle(stateId, decision, choice, reset) {
        var selected = [];

        selected.push(choice.id);

        return EventsWidgetService.process(stateId, 'decision', decision, selected, reset);
      },

      /**
       * Processes the scoring and feedback of
       * state.ChoiceDecision item types that
       * are single answer.
       *
       * @param {string} stateId - ID of the state
       * @param {string} decision - Decision Content
       * @param {string} choices - Choices object
       * @param {boolean} reset - Reset values
       * @return {Promise<Object>} Full feedback object resulting from process
       */
      processMultiple: function processMultiple(stateId, decision, choices, reset) {
        var selected = _(choices)
          .pairs()
          .filter({ 1: true })
          .flatten()
          .without(true)
          .value();

        return EventsWidgetService.process(stateId, 'decision', decision, selected, reset);
      },

      /**
       * Process difficult decision feedback from students.
       *
       * @param {string} state - id of current state
       * @param {string} feedback - student feedback text
       * @param {string} linkedEvent - decision event id that student is giving feedback for
       * @return {Promise<Object>} Copy of feedback event stored to server
       */
      processDifficultDecision: function processDifficultDecision(state, feedback, linkedEvent) {
        return EventsWidgetService.process(
          state,
          'difficult-decision',
          { feedback: feedback },
          [linkedEvent],
          false,
          false
        );
      },

      /**
       * Processes the scoring and feedback of
       * state.ChoiceDecision item types.
       *
       * Feedback properties that are always returned
       * attempts
       * disabled
       * points
       * correct
       * targetId
       * choiceFeedback
       *
       * Feedback properties that are only returned once max attempts reached or correct answer
       * correctFeedback
       * incorrectFeedback
       * rule
       *
       * @param {string} stateId - ID of the state
       * @param {string} decision - Decision Content
       * @param {Array} selectedIds - IDs of the selected choices
       * @param {boolean} reset - Reset values
       * @return {Promise<Object>} Full feedback object resulting from process
       */
      process: function process(stateId, decision, selectedIds, reset) {
        var endpoint;
        var e;

        if (reset) {
          service.disabled = [];
          service.feedback = {
            rule: null,
            attempts: {
              max: 0,
              taken: 0
            },
            disabled: [],
            points: {
              earned: 0,
              total: 0
            },
            correct: {
              earned: [],
              total: 0,
              isCorrect: false
            },
            targetId: '',
            correctFeedback: [],
            incorrectFeedback: [],
            choiceFeedback: null
          };
        }


        endpoint = ModelsService.find('endpoints', 'label', 'events');

        e = {
          label: 'event-' + uuid.v1(),
          content: {
            scored: true,
            decision: decision,
            end: false,
            type: 'decision',
            stateId: stateId,
            reportId: $rootScope.report.id,
            choices: selectedIds
          }
        };

        return ModelsService
          .create(endpoint, e)
          .then(function (response) {
            return EventsWidgetService.capture(response);
          })
          .catch(function (err) {
            console.log(err);
          });
      },

      /**
       * Parses feedback into the formattedModel used
       * in the controller for display.
       *
       * HACK: Added media parameter because server does not attach media all to feedback
       * FIXME: Add all media attributes to feedback on server, and remove parameter here
       *
       * @param {Object} formattedModel - The formattedModel of the current state
       * @param {Object} feedback - Feedback object from scoring event
       * @param {Object} media - New media
       * @param {string} [eventId] - Event that triggered feedback
       * @return {Object} new feedback
       */
      addFeedbackToModel: function addFeedbackToModel(formattedModel, feedback, media, eventId) {
        var newModel = _.cloneDeep(formattedModel);
        // NOTE: this prevents cloneDeep from interfering with $sce.trustAsHtml
        newModel.stem = formattedModel.stem;

        newModel.choices = _.map(newModel.choices, function (n) {
          return _(feedback.rule.content.decision.choices)
            .filter({ id: n.id })
            .reduce(function (all, newProps) {
              return _.assign(all, newProps);
            }, n);
        });

        newModel.feedback = feedback;
        newModel.feedback.media = media;
        newModel.id = eventId;

        return newModel;
      },

      /**
       * Parses out scoring information from decision
       *
       * @param {Object} decision - Decisions object
       * @return {Object} Clean decision object
       */
      sanitizeDecision: function sanitizeDecision(decision) {
        var decisionCopy = _.cloneDeep(decision);

        delete decisionCopy.correctFeedback;
        delete decisionCopy.incorrectFeedbackMedia;
        delete decisionCopy.correctTarget;
        delete decisionCopy.incorrectFeedback;
        delete decisionCopy.incorrectFeedbackMedia;
        delete decisionCopy.incorrectTarget;
        delete decisionCopy.maxAttempts;

        _.forEach(decisionCopy.choices, function (choice) {
          delete choice.correct;
          delete choice.feedback;
          delete choice.feedbackMedia;
          delete choice.correctFeedback;
          delete choice.correctFeedbackMedia;
          delete choice.incorrectFeedback;
          delete choice.incorrectFeedbackMedia;
          delete choice.points;
          delete choice.target;
        });

        return decisionCopy;
      },

      /**
       * Returns the model from the form/editor service
       *
       * @param {Object} model - Model object
       * @param {Object} form - Form options
       * @return {Object} Widget model
       */
      getModel: function getModel(model, form) {
        // make a deep clone of the model
        var modelCopy = _.cloneDeep(model);

        modelCopy.label = form.title;
        modelCopy.content.rules.content.scored = true;
        modelCopy.content.rules.content.totalPoints = 0;
        modelCopy.content.rules.content.decision.stem = form.stem;
        modelCopy.content.rules.content.decision.media = form.media;
        modelCopy.content.rules.content.decision.multiple = form.multiple;
        modelCopy.content.rules.content.decision.scored = true;
        modelCopy.content.rules.content.decision.model = form.model;
        modelCopy.content.rules.content.decision.maxAttempts = form.maxAttempts;
        modelCopy.content.rules.content.decision.correctFeedback = form.correctFeedback;
        modelCopy.content.rules.content.decision.correctFeedbackMedia = form.correctFeedbackMedia;
        modelCopy.content.rules.content.decision.correctTarget = form.correctTarget;
        modelCopy.content.rules.content.decision.incorrectFeedback = form.incorrectFeedback;
        modelCopy.content.rules.content.decision.incorrectFeedbackMedia =
          form.incorrectFeedbackMedia;
        modelCopy.content.rules.content.decision.incorrectTarget = form.incorrectTarget;
        modelCopy.content.rules.content.decision.choices = [];
        _.forEach(form.choices, function (choice, index) {
          var n = {
            id: 'choice-' + uuid.v1()
          };
          modelCopy.content.rules.content.decision.choices[index] = n;
          modelCopy.content.rules.content.decision.choices[index].label = choice.value;
          modelCopy.content.rules.content.decision.choices[index].labelMedia = choice.valueMedia;
          modelCopy.content.rules.content.decision.choices[index].target = choice.target;
          modelCopy.content.rules.content.decision.choices[index].points = choice.points;
          modelCopy.content.rules.content.decision.choices[index].correct = choice.correct;
          modelCopy.content.rules.content.decision.choices[index].feedback = choice.feedback;
          modelCopy.content.rules.content.decision.choices[index].feedbackMedia =
            choice.feedbackMedia;
          modelCopy.content.rules.content.decision.choices[index].correctFeedback =
            choice.correctFeedback;
          modelCopy.content.rules.content.decision.choices[index].correctFeedbackMedia =
            choice.correctFeedbackMedia;
          modelCopy.content.rules.content.decision.choices[index].incorrectFeedback =
            choice.incorrectFeedback;
          modelCopy.content.rules.content.decision.choices[index].incorrectFeedbackMedia =
            choice.incorrectFeedbackMedia;
          modelCopy.content.rules.content.decision.choices[index].points = choice.points;
          modelCopy.content.rules.content.totalPoints += choice.points;
        });

        // set the decision content to that of the rule decision
        // with all of the scoring information removed.
        modelCopy.content.decisions.content = service
          .sanitizeDecision(modelCopy.content.rules.content.decision);

        return modelCopy;
      },

      /**
       * Configures the properties for the form editing process
       *
       * @param {Object} model - Model object
       * @return {Object} Form model object
       */
      configureForm: function configureForm(model) {
        var tinymceOptions = {
          branding: false,
          menubar: false,
          plugins: 'advlist autolink lists link image charmap print preview hr anchor pagebreak table paste',
          toolbar: 'undo redo removeformat spellchecker | formatselect table | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table | link unlink | subscript superscript',
          default_link_target: '_blank'
        };

        var form = { schema: {}, form: {}, model: {} };
        var config = ModelsService.find('schemas', 'label', 'state.choiceDecision');

        // make a deep clone of the model
        var modelCopy = _.cloneDeep(model);

        form.schema = config.content.schema;
        form.form = service.addStatesListToForm(config.content.form);

        // set items individually to determine format
        form.model.title = modelCopy.label;
        form.model.stem = modelCopy.content.rules.content.decision.stem
          || modelCopy.content.decisions.content.stem;
        form.model.media = modelCopy.content.rules.content.decision.media;
        form.model.multiple = modelCopy.content.rules.content.decision.multiple;
        form.model.model = modelCopy.content.rules.content.decision.model;
        form.model.scored = modelCopy.content.rules.content.decision.scored;
        if (modelCopy.content.rules.content.decision.maxAttempts === 0) {
          modelCopy.content.rules.content.decision.maxAttempts = 1;
        }
        form.model.maxAttempts = modelCopy.content.rules.content.decision.maxAttempts;
        form.model.correctFeedback = modelCopy.content.rules.content.decision.correctFeedback;
        form.model.correctFeedbackMedia =
          modelCopy.content.rules.content.decision.correctFeedbackMedia;
        form.model.correctTarget = modelCopy.content.rules.content.decision.correctTarget;
        form.model.incorrectFeedback = modelCopy.content.rules.content.decision.incorrectFeedback;
        form.model.incorrectFeedbackMedia =
          modelCopy.content.rules.content.decision.incorrectFeedbackMedia;
        form.model.incorrectTarget = modelCopy.content.rules.content.decision.incorrectTarget;
        form.model.choices = [];

        // apply custom tinymce options to top level form items
        _.forEach(_.filter(form.form, { type: 'wysiwyg' }), function (formItem) {
          formItem.tinymceOptions = tinymceOptions;
        });

        // apply custom tinymce options to choice items
        _.forEach(_.filter(_.filter(form.form, { key: 'choices' })[0].items, { type: 'wysiwyg' }), function (formItem) {
          formItem.tinymceOptions = tinymceOptions;
        });

        _.forEach(modelCopy.content.rules.content.decision.choices, function (choice, index) {
          var entry = {};
          entry.points = choice.points;
          entry.target = choice.target;
          entry.correct = choice.correct;
          entry.feedback = choice.feedback;
          entry.feedbackMedia = choice.feedbackMedia;
          entry.incorrectFeedback = choice.incorrectFeedback;
          entry.incorrectFeedbackMedia = choice.incorrectFeedbackMedia;
          entry.correctFeedback = choice.correctFeedback;
          entry.correctFeedbackMedia = choice.correctFeedbackMedia;
          entry.value = choice.label || modelCopy.content.decisions.content.choices[index].label;
          entry.valueMedia = choice.labelMedia;

          form.model.choices.push(entry);
        });

        return form;
      },

      /**
       * Returns a form with the StateListTitleMap variable
       * replaced with correct data
       *
       * @param {Array<Object>} form - Form collection
       * @return {Array<Object>} added states
       */
      addStatesListToForm: function addStatesListToForm(form) {
        var reg = new RegExp(/\["StateListTitleMap"\]/, 'g');
        var map = JSON.stringify(StatesService.getStatesListTitleMap());

        return JSON.parse(JSON.stringify(form).replace(reg, map));
      }
    };

    return service;
  }
]);
